import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { LuxuryLayout } from './components/luxury/LuxuryLayout';
import GujaratiGreeting from './components/GujaratiGreeting';
import UpcomingEventPill from './components/UpcomingEventPill';
import LuxuryHome from './pages/luxury/LuxuryHome';
import LuxuryAbout from './pages/luxury/LuxuryAbout';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import LuxuryMembership from './pages/luxury/LuxuryMembership';
import LuxuryUpdateDetails from './pages/luxury/LuxuryUpdateDetails';
import LuxuryAddFamily from './pages/luxury/LuxuryAddFamily';
import LuxuryRenew from './pages/luxury/LuxuryRenew';
import MembershipSuccess from './pages/MembershipSuccess';
import LuxuryDonations from './pages/luxury/LuxuryDonations';
import DonationSuccess from './pages/DonationSuccess';
import SaturdayClubSuccess from './pages/SaturdayClubSuccess';
import LuxuryGallery from './pages/luxury/LuxuryGallery';
import LuxuryVolunteer from './pages/LuxuryVolunteer';
import LuxurySponsors from './pages/luxury/LuxurySponsors';
import LuxurySaturdayClub from './pages/LuxurySaturdayClub';
import LuxuryFAQ from './pages/LuxuryFAQ';
import LuxuryContact from './pages/luxury/LuxuryContact';
import PaymentSuccess from './pages/PaymentSuccess';
import LuxuryPrivacyPolicy from './pages/LuxuryPrivacyPolicy';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Newsletters from './pages/Newsletters';
import NewsletterDetail from './pages/NewsletterDetail';
import VerifyMember from './pages/VerifyMember';
import VerifyTicket from './pages/VerifyTicket';
import MyCard from './pages/MyCard';
import MembershipUpload from './pages/MembershipUpload';
import ClaimMembership from './pages/ClaimMembership';
import ReturningMemberUpload from './pages/ReturningMemberUpload';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const IS_PREVIEW = /preview\.emergentagent\.com$|^localhost$|^127\./.test(window.location.hostname);

// ---- Final Member Database unlock (P1.3): opaque server session token kept in sessionStorage ----
export const dbUnlock = {
  get: () => sessionStorage.getItem('gs_db_unlock') || '',
  set: (t) => { sessionStorage.setItem('gs_db_unlock', t); window.dispatchEvent(new CustomEvent('gs-final-db-unlocked')); },
  clear: () => sessionStorage.removeItem('gs_db_unlock'),
};

const parseError = async (res) => {
  const err = await res.json().catch(() => ({ detail: 'Request failed' }));
  const detail = err.detail;
  if (res.status === 423 || (detail && detail.code === 'FINAL_DB_LOCKED')) {
    dbUnlock.clear();
    window.dispatchEvent(new CustomEvent('gs-final-db-locked'));
    const e = new Error((detail && detail.message) || 'Final Member Database is locked');
    e.code = 'FINAL_DB_LOCKED';
    return e;
  }
  const msg = typeof detail === 'string' ? detail : (detail && (detail.message || JSON.stringify(detail))) || 'Request failed';
  const e = new Error(msg); e.status = res.status;
  return e;
};

const authHeaders = (token, extra = {}) => {
  const h = { 'Authorization': `Bearer ${token}`, ...extra };
  const u = dbUnlock.get();
  if (u) h['X-DB-Unlock'] = u;
  return h;
};

// Staff session expiry: a saved token that the server no longer accepts (401 on /api/admin/*)
// must not leave the portal looking "logged in" with empty screens. Clear it and send the
// user to the login page once, with a clear message.
let sessionExpiredHandled = false;
const handleStaffSessionExpired = () => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  dbUnlock.clear();
  localStorage.removeItem('gs_admin_token');
  localStorage.removeItem('gs_admin_name');
  localStorage.removeItem('gs_admin_role');
  toast.warning('Your staff session has expired — please sign in again.', { id: 'session-expired' });
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  window.location.assign('/admin/login?expired=1' + (tab ? `&tab=${encodeURIComponent(tab)}` : ''));
};

const request = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, opts);
  if (res.status === 401 && path.startsWith('/api/admin') && !path.startsWith('/api/admin/login')
      && opts.headers && opts.headers.Authorization && localStorage.getItem('gs_admin_token')) {
    handleStaffSessionExpired();
    throw new Error('Session expired');
  }
  if (!res.ok) throw await parseError(res);
  return res.json();
};

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  postAuth: (path, body, token) => request(path, { method: 'POST', headers: authHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(body) }),
  getAuth: (path, token) => request(path, { headers: authHeaders(token) }),
  putAuth: (path, body, token) => request(path, { method: 'PUT', headers: authHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(body) }),
  deleteAuth: (path, token) => request(path, { method: 'DELETE', headers: authHeaders(token) }),
  uploadAuth: (path, formData, token) => request(path, { method: 'POST', headers: authHeaders(token), body: formData }),
  // raw fetch with auth + unlock headers (for blobs/images)
  fetchAuth: (path, token, opts = {}) => fetch(`${BACKEND_URL}${path}`, { ...opts, headers: authHeaders(token, opts.headers || {}) }),
};

function MaintenanceGate({ children }) {
  const [paused, setPaused] = React.useState(null);
  React.useEffect(() => {
    // Secret staff bypass: visit any page with ?staffview=gsn2026 once
    const params = new URLSearchParams(window.location.search);
    if (params.get('staffview') === 'gsn2026') {
      localStorage.setItem('gsn_staff_bypass', '1');
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/settings`)
      .then(r => r.json())
      .then(d => setPaused(!!d.site_paused))
      .catch(() => setPaused(false));
  }, []);
  const isAdminRoute = /^\/(admin|verify\/|my-card\/|membership\/upload\/)/.test(window.location.pathname);
  const hasBypass = localStorage.getItem('gsn_staff_bypass') === '1';
  if (paused === null) return null;
  if (paused && !isAdminRoute && !hasBypass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1b3d] px-6" data-testid="maintenance-page">
        <div className="max-w-lg text-center">
          <img src="/icons/icon-192.png" alt="Gujarat Samaj Nottingham" className="w-20 h-20 mx-auto mb-6 rounded-2xl" onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 className="text-3xl font-bold text-[#c9962e] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Gujarat Samaj Nottingham
          </h1>
          <p className="text-white/80 text-lg mb-2">We're briefly offline for updates.</p>
          <p className="text-white/50 text-sm">Please check back shortly. Aabhar — thank you for your patience!</p>
        </div>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <Router>
      <MaintenanceGate>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-[hsl(var(--background))]">
        <Toaster position="top-right" richColors />
        {/* Top-left notice stack: greeting pill + next upcoming event pill (stack closes up when one is dismissed) */}
        <div className="fixed top-24 left-4 z-40 flex flex-col items-start gap-3 pointer-events-none" data-testid="notice-stack">
          <GujaratiGreeting />
          <UpcomingEventPill />
        </div>
        
        {/* Demo badge — preview/local builds only, never on the live site */}
        {IS_PREVIEW && (
          <div
            className="fixed bottom-4 left-4 z-50 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-1.5 shadow-md"
            data-testid="demo-badge"
          >
            <p className="text-xs font-medium text-gray-600">
              Viewing Demo Version
            </p>
          </div>
        )}
        
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
          
          {/* Main Routes - Luxury Design */}
          <Route path="/*" element={
            <LuxuryLayout>
              <Routes>
                <Route path="/" element={<LuxuryHome />} />
                <Route path="/about" element={<LuxuryAbout />} />
                <Route path="/events" element={<Events />} />
                <Route path="/events/:eventId" element={<EventDetail />} />
                <Route path="/newsletters" element={<Newsletters />} />
                <Route path="/newsletters/:newsletterId" element={<NewsletterDetail />} />
                <Route path="/membership" element={<LuxuryMembership />} />
                <Route path="/update-details" element={<LuxuryUpdateDetails />} />
                <Route path="/membership/success" element={<MembershipSuccess />} />
                <Route path="/membership/add-family" element={<LuxuryAddFamily />} />
                <Route path="/membership/renew" element={<LuxuryRenew />} />
                <Route path="/membership/returning" element={<ReturningMemberUpload />} />
                <Route path="/membership/upload/:token" element={<MembershipUpload />} />
                <Route path="/claim/:token" element={<ClaimMembership />} />
                <Route path="/my-card/:token" element={<MyCard />} />
                <Route path="/verify/member/:token" element={<VerifyMember />} />
                <Route path="/verify/ticket/:token" element={<VerifyTicket />} />
                <Route path="/donations" element={<LuxuryDonations />} />
                <Route path="/donations/success" element={<DonationSuccess />} />
                <Route path="/gallery" element={<LuxuryGallery />} />
                <Route path="/gallery/:albumId" element={<LuxuryGallery />} />
                <Route path="/volunteer" element={<LuxuryVolunteer />} />
                <Route path="/sponsors" element={<LuxurySponsors />} />
                <Route path="/saturday-club" element={<LuxurySaturdayClub />} />
                <Route path="/saturday-club/success" element={<SaturdayClubSuccess />} />
                <Route path="/faq" element={<LuxuryFAQ />} />
                <Route path="/contact" element={<LuxuryContact />} />
                <Route path="/privacy-policy" element={<LuxuryPrivacyPolicy />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
              </Routes>
            </LuxuryLayout>
          } />
        </Routes>
      </div>
      </MaintenanceGate>
    </Router>
  );
}

export default App;
